import * as textile from 'textile-js';
import { Tableau } from '../../common/utilities/TableauLog';
import type { ISanitizer } from '../../commonInfra/utils/Sanitize';

export namespace TextileHelper {
  /*
   * We don't have true thumbnail metadata for projects.
   * What we have are image urls in the description field.
   * This expression matches any description that *ends* with an image url.
   *
   * It must be bounded with bangs (!)
   * It must not contain whitespace
   * It ignores any trailing whitespace
   *
   * The capture group in the regex is necessary for the ProjectCard's getThumbnailUrlFromDescription function.
   */
  export const TRAILING_IMAGE = /!([^\s]+)!\s*$/;

  /*
   * 2021-02-02. In the past, processing deeply nested lists in textile caused perf problems.
   * This issue has been addressed in our textile parser, so we are removing our special-case code.
   * Should something similar occur again, see TFS868008
   */

  // This converter uses textile-js (https://github.com/borgar/textile-js) to perform the conversion.
  function convertTextileToHtml(text: string): string {
    return replaceInsWithUnderline(textile(text));
  }

  function replaceInsWithUnderline(markup: string): string {
    return replaceAll(replaceAll(markup, '<ins', '<u'), '</ins>', '</u>');
  }

  function replaceAll(str: string, find: string, replace: string): string {
    const regExpFind = new RegExp(find.replace(/([.*+?^=!:${}()|\[\]\/\\])/g, '\\$1'), 'g');

    return str.replace(regExpFind, replace);
  }

  // Uses ideas from https://github.com/btford/angular-markdown-directive/blob/master/markdown.js
  export function getSanitizedHtmlFromTextile(
    textile: string,
    $sanitize: ISanitizer,
  ) {
    let html = '';
    if (textile) {
      textile = textile.trim();
      try {
        html = $sanitize(convertTextileToHtml(textile));
      } catch (ex) {
        Tableau.Logger().error(ex.name + ': ' + ex.message);
        Tableau.Logger().object(ex);
        return null;
      }
    }
    return html;
  }

  export function getPlainTextFromTextile(
    textile: string,
    $sanitize: ISanitizer,
  ): string {
    const html = getSanitizedHtmlFromTextile(textile, $sanitize);
    return getPlainTextFromSanitizedHtml(html);
  }

  /*
   * 2018-12-17 - This function has an unintended side effect: if the HTML contains img tags
   * (or other resources), the browser will download those resources when it "renders" the JQuery
   * element, even though we're just trying to get the plain text content. We are aware of this, but
   * have decided that it's not currently worth fixing (see TFS 853479).
   */
  export function getPlainTextFromSanitizedHtml(html: string): string {
    if (html !== null) {
      try {
        return $(html).text().trim();
      } catch (ex) {
        Tableau.Logger().error(ex.name + ': ' + ex.message);
        Tableau.Logger().object(ex);
        return '';
      }
    }
    return '';
  }
}
